import {
	Checkbox,
	ComboBox,
	FlexColumn,
	FlexRow,
	IconTooltip,
	Label,
	Modal,
	Separator,
	Text,
	TextField,
} from '@forecast-it/design-system';
import React from 'react';
import {useIntl} from 'react-intl';
import {
	ClientContactFormValues,
	ClientContactStatus,
	EmailUniqueValidator,
	useClientContactForm,
} from './hooks/useClientContactForm';
import {DisableableSection, GuestUserField, LabelWithTooltipField, StatusNotice} from './ClientContactModal.styles';
import ProjectAccessField from './ProjectAccessField';
import ViewMoreLess from '@/components/view-more-less/view-more-less';
import type {ProjectOption} from './ClientContactModal';

export type EditClientContactModalProps = {
	clientName: string;
	projectOptions: ProjectOption[];
	isProjectsLoading: boolean;
	initialValues: ClientContactFormValues;
	isGuestUser: boolean;
	onGuestUserToggle: () => void;
	onClose: () => void;
	onSubmit: (values: ClientContactFormValues) => void;
	isSubmitting?: boolean;
	validateEmailUnique?: EmailUniqueValidator;
};

const EditClientContactModal = ({
	clientName,
	projectOptions,
	isProjectsLoading,
	initialValues,
	isGuestUser,
	onGuestUserToggle,
	onClose,
	onSubmit,
	isSubmitting = false,
	validateEmailUnique,
}: EditClientContactModalProps): JSX.Element => {
	const {formatMessage} = useIntl();

	const {
		firstName,
		handleFirstNameChange,
		firstNameError,
		lastName,
		handleLastNameChange,
		lastNameError,
		email,
		handleEmailChange,
		emailError,
		handleEmailBlur,
		jobTitle,
		handleJobTitleChange,
		phone,
		handlePhoneChange,
		status,
		handleStatusChange,
		handleGuestUserToggle,
		availableProjects,
		selectedProjects,
		handleAddProject,
		handleRemoveProject,
		handleSubmit,
	} = useClientContactForm(projectOptions, isGuestUser, onGuestUserToggle, onSubmit, validateEmailUnique, initialValues);

	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={onClose}>
			<Modal.Title>{formatMessage({id: 'client_contact.edit_contact.modal_title'})}</Modal.Title>
			<Modal.Content>
				<div data-cy="edit-client-contact-modal">
					<FlexColumn gap={'l'}>
						<LabelWithTooltipField>
							<ComboBox
								aria-label={formatMessage({id: 'client_contact.field.client'})}
								defaultItems={[{id: 'static', name: clientName}]}
								value={'static'}
								onChange={() => undefined}
								disabled={true}
								width={'flex'}
							>
								{item => (
									<ComboBox.Item textValue={item.name}>
										<Text noWrap>{item.name}</Text>
									</ComboBox.Item>
								)}
							</ComboBox>
							<FlexRow gap={'xs'} alignItems={'center'}>
								<Label>{formatMessage({id: 'client_contact.field.client'})}</Label>
								<IconTooltip>
									<IconTooltip.Item
										title={formatMessage({id: 'client_contact.tooltip.client.title'})}
										details={formatMessage({id: 'client_contact.tooltip.client'})}
									/>
								</IconTooltip>
							</FlexRow>
						</LabelWithTooltipField>

						<FlexRow gap={'l'}>
							<TextField
								data-cy="edit-client-contact-first-name-input"
								label={formatMessage({id: 'client_contact.field.first_name'})}
								value={firstName}
								onValueChange={handleFirstNameChange}
								hasError={!!firstNameError}
								errorText={firstNameError || undefined}
								size="large"
								width="flex"
							/>
							<TextField
								data-cy="edit-client-contact-last-name-input"
								label={formatMessage({id: 'client_contact.field.last_name'})}
								value={lastName}
								onValueChange={handleLastNameChange}
								hasError={!!lastNameError}
								errorText={lastNameError || undefined}
								size="large"
								width="flex"
							/>
						</FlexRow>

						<TextField
							data-cy="edit-client-contact-email-input"
							label={formatMessage({id: 'client_contact.field.email'})}
							value={email}
							onValueChange={handleEmailChange}
							onBlur={handleEmailBlur}
							hasError={!!emailError}
							errorText={emailError || undefined}
							size="large"
							width="flex"
						/>

						<FlexRow gap={'l'}>
							<FlexColumn gap={'xs'} flex={'1'}>
								<LabelWithTooltipField>
									<ComboBox
										aria-label={formatMessage({id: 'client_contact.field.status'})}
										data-cy="edit-client-contact-status-combobox"
										defaultItems={[
											{id: 'ACTIVE', name: formatMessage({id: 'client_contact.status.active'})},
											{id: 'INACTIVE', name: formatMessage({id: 'client_contact.status.inactive'})},
										]}
										value={status}
										onChange={key => handleStatusChange(key as ClientContactStatus)}
										width={'flex'}
										menuTrigger="focus"
									>
										{item => (
											<ComboBox.Item textValue={item.name}>
												<Text noWrap>{item.name}</Text>
											</ComboBox.Item>
										)}
									</ComboBox>
									<Label>{formatMessage({id: 'client_contact.field.status'})}</Label>
								</LabelWithTooltipField>
								{status === 'INACTIVE' && (
									<StatusNotice data-cy="edit-client-contact-status-inactive-notice">
										{formatMessage({id: 'client_contact.edit_contact.status_inactive_notice'})}
									</StatusNotice>
								)}
							</FlexColumn>
							<div style={{flex: 1}} />
						</FlexRow>

						<ViewMoreLess
							expandLabel={formatMessage({id: 'client_contact.field.view_more'})}
							collapseLabel={formatMessage({id: 'client_contact.field.view_less'})}
						>
							<FlexColumn gap={'l'}>
								<TextField
									label={formatMessage({id: 'client_contact.field.job_title'})}
									value={jobTitle}
									onValueChange={handleJobTitleChange}
									size="large"
									width="flex"
								/>
								<TextField
									label={formatMessage({id: 'client_contact.field.phone_number_optional'})}
									value={phone}
									onValueChange={handlePhoneChange}
									size="large"
									width="flex"
								/>
								<Separator />
							</FlexColumn>
						</ViewMoreLess>

						<DisableableSection disabled={status === 'INACTIVE'} data-cy="edit-client-contact-guest-section">
							<FlexColumn gap={'l'}>
								<GuestUserField>
									<Checkbox
										checked={isGuestUser}
										onChange={handleGuestUserToggle}
										aria-label={formatMessage({id: 'client_contact.field.guest_user'})}
										disabled={status === 'INACTIVE'}
									/>
									<Label>{formatMessage({id: 'client_contact.field.guest_user'})}</Label>
									<IconTooltip>
										<IconTooltip.Item
											title={formatMessage({id: 'client_contact.field.guest_user'})}
											details={formatMessage({id: 'client_contact.tooltip.guest_user'})}
										/>
									</IconTooltip>
								</GuestUserField>

								{isGuestUser && (
									<ProjectAccessField
										availableProjects={availableProjects}
										selectedProjects={selectedProjects}
										isLoading={isProjectsLoading}
										onAdd={handleAddProject}
										onRemove={handleRemoveProject}
									/>
								)}
							</FlexColumn>
						</DisableableSection>
					</FlexColumn>
				</div>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					dataCy="edit-client-contact-submit-button"
					onPress={handleSubmit}
					disabled={isSubmitting}
				>
					{formatMessage({id: 'client_contact.edit_contact.submit_button'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={onClose} disabled={isSubmitting}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default EditClientContactModal;
